// src/pages/Customer-items/AddEditCustomerItems.tsx
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Formik, Form, Field } from "formik";

import { CustomerUserTypes } from "../CustomerTypes";
import { RootState } from "../../../redux/store";
import { getApi, postApi } from "../../../apis/api";
import { useMessage } from "../../../context/MessageContext";
import { Loader } from "../../../components/loaders/loaders";
import MutliDropdown from "../../../components/shared/MutliDropdown";
import { SingleDropdown } from "../../../components/shared";
import useFormChangeHandler from "../../../hooks/useFormChangeHandler";
import ConfirmModal from "../../../components/modals/ConfirmModal";
import {
  CUSTOMER_MANAGEMENT_MESSAGES,
  GENERAL_ERROR_MESSAGE,
} from "../../../utils/message";
import { FREIGHT_CLASS } from "../../../utils/constants";

interface ColumnTypes {
  key: string;
  title: string;
}

const customerColumns: ColumnTypes[] = [
  { key: "name", title: "Customer Name" },
  { key: "customerNumber", title: "Number" },
];

interface AgentCustomerDropdown {
  id?: string;
  name: string | null;
  customerNumber: string;
  customerLevel?: string;
}

// HU Type option
type PackageTypeOption = { id: string; name: string };

type ItemFormSlice = {
  description: string;
  packageType: string; // we store NAME here for display
  weight: string | number;
  weightUnit: "lbs" | "kgs";
  length: string | number;
  width: string | number;
  height: string | number;
  unit: "IN" | "CM";
  stackable: "Yes" | "No";
  skuNumber: string;
  itemClass: string;
  nmfc: string;
  comments: string;
  customerId?: string;     // Track main customer
  subCustomerId?: string;  // Track sub-customer
};

const ITEMS_ENDPOINT = "/customer/saveddetails/addupdateitems";
const ITEM_DETAIL_ENDPOINT = "/customer/saveddetails/items/"; // GET for edit prefill

const DEFAULT_PACKAGE_TYPES: PackageTypeOption[] = [
  { id: "PALLET", name: "PALLET" },
  { id: "PLT", name: "PLT" },
  { id: "SKID", name: "SKID" },
  { id: "BOX", name: "BOX" },
  { id: "CARTON", name: "CARTON" },
  { id: "CRATE", name: "CRATE" },
];

const AddEditCustomerItems = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { setMessage } = useMessage();

  const { userRole, isUserOfOmniMove, customerLevel } = useSelector(
    (state: RootState) => state.auth
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [parentCustomerId, setParentCustomerId] = useState<string>();
  const [subCustomerLoaded, setSubCustomerLoaded] = useState<boolean>(false);
  const [customerDropdownData, setCustomerDropdownData] = useState<
    AgentCustomerDropdown[]
  >([]);
  const [assCustomerNameDropdown, setAssCustomerNameDropdown] = useState<
    AgentCustomerDropdown[]
  >([]);
  const [customerRowData, setCustomerRowData] = useState<AgentCustomerDropdown>(
    {
      id: "",
      name: "",
      customerNumber: "",
      customerLevel: "",
    }
  );
  const [assCustomerRowData, setAssCustomerRowData] =
    useState<AgentCustomerDropdown>({
      id: "",
      name: "",
      customerNumber: "",
      customerLevel: "",
    });

  // Track if user actually changed anything (needed for EDIT mode button enablement)
  // const [userChanged, setUserChanged] = useState<boolean>(false);

  // API + fallback HU types
  const [apiPackageType, setApiPackageType] = useState<PackageTypeOption[]>([]);
  const [packageTypes, setPackageTypes] = useState<PackageTypeOption[]>(
    DEFAULT_PACKAGE_TYPES
  );

  // base form values
  const baseInitialUserValues: CustomerUserTypes = {
    firstName: "",
    lastName: "",
    email: "",
    countryCallingCode: "+1",
    phoneNumber: "",
    userRole: "employee",
    customerCheck:
      (userRole === "admin" ||
        userRole === "customersuperadmin" ||
        userRole === "manager") &&
      !isUserOfOmniMove
        ? true
        : false,
  };

  const baseItemSlice: ItemFormSlice = {
    description: "",
    packageType: "", // mandatory; stores NAME
    weight: "",
    weightUnit: "lbs",
    length: "",
    width: "",
    height: "",
    unit: "IN",
    stackable: "No",
    skuNumber: "",
    itemClass: "",
    nmfc: "",
    comments: "",
    customerId: "",
    subCustomerId: "",
  };

  const [initialValues, setInitialValues] = useState<
    CustomerUserTypes & ItemFormSlice
  >({
    ...baseInitialUserValues,
    ...baseItemSlice,
  });

  const {
    checkInputChange,
    handleConfirmNavigation,
    handleCancelNavigation,
    showModal,
    handleUpdateNavigation,
  } = useFormChangeHandler();

  // Keep parentCustomerId in sync
  // useEffect(() => {
  //   if (customerRowData && customerRowData.id && !id) {
  //     setParentCustomerId(customerRowData.id);
  //   }
  // }, [customerRowData, id]);
  // Keep parentCustomerId in sync for BOTH Add and Edit
  useEffect(() => {
    if (customerRowData && customerRowData.id) {
      setParentCustomerId(customerRowData.id);
    }
  }, [customerRowData]);

  // Fetch Handling Unit Types from API (optional; fallback retained)
  const getApiPackageData = async () => {
    try {
      const url = `/service/packagetype?from=order`;
      const apiResponse = await getApi<any>(url);
      if (!apiResponse?.success) return;

      const packageData: PackageTypeOption[] =
        (apiResponse?.data || [])
          .filter((item: any) => item.isActive && !item.isDeleted)
          .map((item: any) => ({
            id: item.id ?? item.packageCode ?? "",
            name: item.packageCode || "",
          })) || [];

      setApiPackageType(packageData);
    } catch {
      // ignore; fallback options remain
    }
  };

  // Call API once
  useEffect(() => {
    getApiPackageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Merge API + defaults (unique by name, case-insensitive)
  useEffect(() => {
    const seen = new Set<string>();
    const merged = [...apiPackageType, ...DEFAULT_PACKAGE_TYPES].filter(
      (opt) => {
        const key = (opt?.name || "").trim().toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      }
    );
    setPackageTypes(merged);
  }, [apiPackageType]);

  /** ========= EDIT prefill ========= */
  useEffect(() => {
    if (!id) return;

    const navState: any =
      (location &&
        (location.state?.row || location.state?.data || location.state)) ||
      null;

    const normalizePkgToPreferName = (pkg: any): string => {
      // Prefer NAME for UI storage
      if (!pkg) return "";
      if (typeof pkg === "string") return pkg;
      if (typeof pkg === "object") return pkg.name || pkg.id || "";
      return "";
    };

    const applyRowToForm = (row: any) => {
      if (!row) return;
      let mainCustomerId = "";
      let subCustomerId = "";
      // dimensions
      let L = "",
        W = "",
        H = "";
      if (row.lengthVal || row.widthVal || row.heightVal) {
        L = (row.lengthVal ?? "").toString();
        W = (row.widthVal ?? "").toString();
        H = (row.heightVal ?? "").toString();
      } else if (
        row.dimension &&
        typeof row.dimension === "string" &&
        row.dimension.includes("×")
      ) {
        const parts = row.dimension.split("×").map((p: string) => p.trim());
        [L, W, H] = [parts[0] || "", parts[1] || "", parts[2] || ""];
      }

      // infer units
      const inferredWeightUnit: "lbs" | "kgs" =
        row.unit === "CM" ? "kgs" : "lbs";
      const inferredDimUnit: "IN" | "CM" = row.unit === "CM" ? "CM" : "IN";
      // set customer/sub-customer
      if (row.customer && typeof row.customer === "object") {
        const parent = row.customer.parentCustomer;
        if (parent && row.customer.customerLevel === "third") {
          setCustomerRowData({
            id: parent.id || "",
            name: parent.name || "",
            customerNumber: parent.customerNumber || "",
            customerLevel: parent.customerLevel || "",
          });
          setParentCustomerId(parent.id || "");
          mainCustomerId = parent.id || "";        
          subCustomerId = row.customer.id || ""; 
          const assItemCustomer = {
            id: row.customer.id || "",
            name: row.customer.name || "",
            customerNumber: row.customer.customerNumber || "",
            customerLevel: row.customer.customerLevel || "",
          };
          setTimeout(() => {
            setAssCustomerRowData(assItemCustomer);
          }, 100);
        } else {
          setCustomerRowData({
            id: row.customer.id || "",
            name: row.customer.name || "",
            customerNumber: row.customer.customerNumber || "",
            customerLevel: row.customer.customerLevel || "",
          });
          mainCustomerId = row.customer.id || "";
        }
      } else if (row.customerId || row.customerName) {
        setCustomerRowData((prev) => ({
          ...prev,
          id: row.customerId || "",
          name: row.customerName || "",
          customerNumber: "",
          customerLevel: "",
        }));
         mainCustomerId = row.customerId || "";
      }

      setInitialValues((prev) => ({
        ...prev,
        description: row.itemName || row.description || "",
        packageType: normalizePkgToPreferName(row.packageType), // store NAME when possible
        // weight: (row.weight ?? "").toString(),
        weight: row.weight ? Number(row.weight) : "",
        weightUnit: inferredWeightUnit,
        // length: L,
        // width: W,
        // height: H,
        length: Number(L) || "",
        width: Number(W) || "",
        height: Number(H) || "",
        unit: inferredDimUnit,
        stackable:
          row.stackable === "true" || row.stackable === true ? "Yes" : "No",
        skuNumber: row.itemSKU || row.skuNumber || "",
        itemClass: row.itemClass || "",
        nmfc: row.itemNmfcNo || row.nmfc || "",
        comments: row.comments || "",
        customerId: mainCustomerId,     // ← NEW
        subCustomerId: subCustomerId,
      }));
    };

    if (navState) {
      applyRowToForm(navState);
      return;
    }

    // Fallback: fetch by id on hard reload
    (async () => {
      try {
        setLoading(true);
        const resp = await getApi<any>(`${ITEM_DETAIL_ENDPOINT}${id}`);
        if (resp?.success && resp?.data) {
          const it = resp.data;
          const rowShape = {
            id: it.id,
            customer: it?.customer || null,
            customerId: it?.customer?.id || "",
            customerName: it?.customer?.name || "",
            itemName: it?.description || "",
            itemSKU: it?.skuNumber || "",
            weight: it?.weight ? Number(it?.weight) : "",
            // lengthVal: it?.length,
            // widthVal: it?.width,
            // heightVal: it?.height,
            lengthVal: it?.length ? Number(it?.length) : "",
            widthVal: it?.width ? Number(it?.width) : "",
            heightVal: it?.height ? Number(it?.height) : "",
            unit: it?.unit, // IN | CM
            itemClass: it?.itemClass,
            itemNmfcNo: it?.nmfc,
            comments: it?.comments,
            handlingUnit: it?.handlingUnit,
            stackable: it?.stackable, // "true"/"false" or boolean
            packageType: it?.packageType, // may be string or {id,name}
          };
          applyRowToForm(rowShape);
        } else {
          setMessage(CUSTOMER_MANAGEMENT_MESSAGES.ITEM_LOAD_ERROR, "error");
        }
      } catch {
        setMessage(GENERAL_ERROR_MESSAGE, "error");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // After options load, if initial packageType is an ID, switch it to NAME for display
  useEffect(() => {
    setInitialValues((prev) => {
      const cur = prev.packageType;
      if (!cur) return prev;
      // if already a name, keep
      const byName = packageTypes.find(
        (p) => p.name?.toLowerCase() === cur.toLowerCase()
      );
      if (byName) return prev;
      // otherwise try match id
      const byId = packageTypes.find((p) => String(p.id) === String(cur));
      if (byId) return { ...prev, packageType: byId.name };
      return prev;
    });
  }, [packageTypes]);

  // When initial values are (re)set (e.g., prefill), we haven't changed anything yet
  // useEffect(() => {
  //   setUserChanged(false);
  // }, [initialValues]);

  // Build payload for save
  const buildItemsPayload = (values: CustomerUserTypes & ItemFormSlice) => {
    const customerId = assCustomerRowData?.id || customerRowData?.id || "";

    const stackableStr = values.stackable === "Yes" ? true : false;
    const weightUnitApi = values.weightUnit === "kgs" ? "Kgs" : "Lbs";
    const unitApi = values.unit;

    // Convert NAME (stored in form) to the API value (id if present, else name)
    const pkg =
      packageTypes.find((p) => p.name === values.packageType) ||
      packageTypes.find((p) => String(p.id) === values.packageType);
    const packageTypeForApi = pkg?.name ?? values.packageType ?? "";

    return {
      customerId,
      description: values.description || "",
      handlingUnit: "",
      quantity: "",
      weight: values.weight || "",
      length: values.length || "",
      width: values.width || "",
      height: values.height || "",
      unit: unitApi,
      itemClass: values.itemClass || "",
      comments: values.comments || "",
      nmfc: values.nmfc || "",
      packageType: packageTypeForApi,
      stackable: stackableStr,
      linearFeet: "",
      density: "",
      cbm: "",
      weightUnit: weightUnitApi,
      linearFeetStackable: "false",
      skuNumber: values.skuNumber || "",
      barCodeNumber: "",
      packageArray: [] as any[],
      id: id || undefined,
    };
  };

  const handleSubmit = async (values: CustomerUserTypes & ItemFormSlice) => {
    const itemsPayload = buildItemsPayload(values);

    if (!itemsPayload.customerId) {
      setMessage(
        CUSTOMER_MANAGEMENT_MESSAGES.ITEM_CUSTOMER_REQUIRED,
        "error"
      );
      return;
    }
    if (!values.packageType) {
      setMessage(
        CUSTOMER_MANAGEMENT_MESSAGES.ITEM_HANDLING_UNIT_REQUIRED,
        "error"
      );
      return;
    }

    try {
      setLoading(true);
      const apiResponse = await postApi(ITEMS_ENDPOINT, itemsPayload);
      if (apiResponse?.success) {
        const successMsg = id
          ? CUSTOMER_MANAGEMENT_MESSAGES.ITEM_UPDATE_SUCCESS
          : CUSTOMER_MANAGEMENT_MESSAGES.ITEM_ADD_SUCCESS;
        setMessage(successMsg, "success");
        handleUpdateNavigation();
        // setUserChanged(false); // reset after successful save/update
        navigate("/customer-items");
      } else {
        setMessage(
          apiResponse?.error?.message || GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch {
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => navigate("/customer-items");

  // Mandatory fields readiness
  const mandatoryReady = (values: CustomerUserTypes & ItemFormSlice) => {
    const hasCustomer = Boolean(assCustomerRowData?.id || customerRowData?.id);

    const hasDescription = (values.description || "").trim().length > 0;
    const hasPackageType = (values.packageType || "").trim().length > 0; // mandatory
    const hasWeight = (values.weight || "").toString().trim().length > 0;

    const l = (values.length || "").toString().trim();
    const w = (values.width || "").toString().trim();
    const h = (values.height || "").toString().trim();
    const hasDimension = l.length > 0 && w.length > 0 && h.length > 0;

    const hasItemClass = (values.itemClass || "").toString().trim().length > 0;

    return (
      hasCustomer &&
      hasDescription &&
      hasPackageType &&
      hasWeight &&
      hasDimension &&
      hasItemClass
    );
  };

  // Enable/Disable Save/Update
  const isButtonDisabled = (
    values: CustomerUserTypes & ItemFormSlice,
    // didChange: boolean
    dirty: boolean
  ) => {
    // superadmin should NEVER be blocked
    if (userRole === "superadmin") return false;

    const ready = mandatoryReady(values);

    // ADD mode: same as before (must satisfy mandatory rules)
    if (!id) return !ready;

    // EDIT mode: enforce BOTH (mandatory ok) AND (user actually changed something)
    // return !(ready && dirty);

    return !(ready && dirty);
  };

  // Sync Unit with Weight Unit (LBS->IN, KGS->CM)
  const syncUnitForWeight = (
    setFieldValue: (field: string, val: any) => void,
    weightUnit: "lbs" | "kgs"
  ) => {
    if (weightUnit === "lbs") setFieldValue("unit", "IN");
    if (weightUnit === "kgs") setFieldValue("unit", "CM");
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ values, setFieldValue, submitForm, dirty }) => (
          <Form
            // onChange={(e) => {
            //   // mark as changed for EDIT mode & keep your existing navigation guard
            //   if (!userChanged) setUserChanged(true);
            //   checkInputChange();
            // }}
          >
            <div className="details">
              <div className="form">
                <section className="form__section">
                  <h1 className="form__section__title">Customer Items</h1>

                  {/* Customer & Sub Customer (behaves like AddEditCustomerAddress even in EDIT) */}
                  {customerLevel === "first" &&
                    (userRole === "admin" ||
                      userRole === "customersuperadmin" ||
                      userRole === "manager") && (
                      <div className="formRow formRow__2">
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label">
                              Customer Name{" "}
                              <span className="mandatory-mark">*</span>
                            </label>
                            <MutliDropdown
                              columns={customerColumns}
                              data={customerDropdownData}
                              setData={setCustomerDropdownData}
                              captionTitles="Select Customer"
                              rowData={customerRowData}
                              setRowData={(row) => {
                                setCustomerRowData(row);
                                // reset sub-customer when parent changes
                                setFieldValue("customerId", row?.id || "");
                                setAssCustomerRowData({
                                  id: "",
                                  name: "",
                                  customerNumber: "",
                                  customerLevel: "",
                                });
                                setFieldValue("subCustomerId", "");
                                setParentCustomerId(row?.id || "");
                                // setUserChanged(true); // <-- ensure EDIT change detection
                              }}
                              displayColumn="name"
                              apiUrl={"/customer"}
                              mandatory
                              enableRemove
                              type="order"
                              status={3}
                              extendedUrl={{ requestFrom: "new" }}
                            />
                          </div>
                        </div>
                        <div className="formCol">
                          <MutliDropdown
                            columns={customerColumns}
                            data={assCustomerNameDropdown}
                            setData={setAssCustomerNameDropdown}
                            captionTitles="Select Sub Customer"
                            rowData={
                              customerRowData?.customerLevel !== "second"
                                ? ("" as any)
                                : assCustomerRowData
                            }
                            setRowData={(row) => {
                              setAssCustomerRowData(row);
                              setFieldValue("subCustomerId", row?.id || "");
                              // setUserChanged(true);
                            }}
                            displayColumn="name"
                            apiUrl={parentCustomerId ? "/customer" : ""}
                            labelTitle="Sub Customer Name"
                            enableRemove
                            mandatory={false}
                            parentCustomerId={parentCustomerId}
                            disable={
                              customerRowData?.customerLevel !== "second"
                            }
                            extendedUrl={{ requestFrom: "new" }}
                          />
                        </div>
                      </div>
                    )}

                  {customerLevel === "second" &&
                    (userRole === "admin" ||
                      userRole === "customersuperadmin" ||
                      userRole === "manager") && (
                      <div className="formRow formRow__2 mb_0">
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label">
                              Customer <span className="mandatory-mark">*</span>
                            </label>
                            <MutliDropdown
                              columns={customerColumns}
                              data={customerDropdownData}
                              setData={setCustomerDropdownData}
                              captionTitles="Select Customer"
                              rowData={customerRowData}
                              setRowData={(row) => {
                                setCustomerRowData(row);
                                setParentCustomerId(row?.id || "");
                                setFieldValue("customerId", row?.id || "");
                                // setUserChanged(true); // <-- ensure EDIT change detection
                              }}
                              displayColumn="name"
                              apiUrl={"/customer"}
                              type="user"
                              enableRemove
                              status={1}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                  {/* --- Customer Items fields --- */}
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label">
                          Description <span className="mandatory-mark">*</span>
                        </label>
                        <Field
                          name="description"
                          className="form__input form__input--sm"
                          placeholder="Description"
                        />
                      </div>
                    </div>

                    {/* Handling Unit TYPE (searchable dropdown, shows NAME) */}
                    <div className="formCol">
                      <div className="form__Field">
                        <SingleDropdown
                          fieldName="packageType"
                          selectedItem={
                            packageTypes.find(
                              (o) =>
                                o.name?.toLowerCase() ===
                                (values.packageType || "").toLowerCase()
                            ) || null
                          }
                          setSelectedData={(_fieldName, item) => {
                            // store NAME to show in UI
                            const name = item?.name ?? "";
                            // if (!userChanged) setUserChanged(true); // mark change
                            setFieldValue("packageType", name);
                          }}
                          staticData={packageTypes}
                          labelTitle={"Handling Unit Type"}
                          placeholder="-Select-"
                          keyBindName="name"
                          mandatory
                          disable={false}
                          customStyle={{ minWidth: "1px" }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="formRow formRow__2">
                    <div className="formRow formRow__2">
                      <div className="formCol">
                        <div className="form__Field">
                          <label className="form__label">
                            Weight <span className="mandatory-mark">*</span>
                          </label>
                          <Field
                            name="weight"
                            type="number"
                            className="form__input form__input--sm"
                            placeholder="Weight"
                          />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label className="form__label">Type</label>
                          <select
                            className="form__select form__select--sm form__select--order"
                            value={values.weightUnit}
                            onChange={(e) => {
                              const unit = e.target.value as "lbs" | "kgs";
                              // if (!userChanged) setUserChanged(true); // mark change
                              setFieldValue("weightUnit", unit);
                              syncUnitForWeight(setFieldValue, unit);
                            }}
                          >
                            <option value="lbs">LBS</option>
                            <option value="kgs">KGS</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="formRow formRow__2">
                      <div className="formCol">
                        <div className="form__Field">
                          <label className="form__label">
                            Dimension <span className="mandatory-mark">*</span>
                          </label>
                          <div className="formRow formRow--flex">
                            <div className="formRow formRow__3">
                              <div className="formCol">
                                <Field
                                  name="length"
                                  type="number"
                                  className="form__input form__input--sm"
                                  placeholder="L"
                                />
                              </div>
                              <div className="formCol">
                                <Field
                                  name="width"
                                  type="number"
                                  className="form__input form__input--sm"
                                  placeholder="W"
                                />
                              </div>
                              <div className="formCol">
                                <Field
                                  name="height"
                                  type="number"
                                  className="form__input form__input--sm"
                                  placeholder="H"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="formCol">
                        <div className="form__Field">
                          <label className="form__label">Unit</label>
                          <select
                            className="form__select form__select--sm form__select--order form__select--disabled"
                            value={values.unit}
                            onChange={(e) => {
                              // if (!userChanged) setUserChanged(true); // mark change
                              setFieldValue("unit", e.target.value);
                            }}
                            disabled
                          >
                            <option value="IN">IN</option>
                            <option value="CM">CM</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__4">
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label">Stack</label>
                        <select
                          className="form__select form__select--sm form__select--order"
                          value={values.stackable}
                          onChange={(e) => {
                            // if (!userChanged) setUserChanged(true); // mark change
                            setFieldValue("stackable", e.target.value);
                          }}
                        >
                          <option value="" disabled>
                            -Select-
                          </option>
                          <option value="No">No</option>
                          <option value="Yes">Yes</option>
                        </select>
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label">Item SKU Number</label>
                        <Field
                          name="skuNumber"
                          className="form__input form__input--sm"
                          placeholder="Item SKU Number"
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label">
                          Item Class <span className="mandatory-mark">*</span>
                        </label>
                        <select
                          className="form__select form__select--sm form__select--order"
                          value={values.itemClass}
                          onChange={(e) => {
                            // if (!userChanged) setUserChanged(true); // mark change
                            setFieldValue("itemClass", e.target.value);
                          }}
                        >
                          <option value="">-Select-</option>
                          {FREIGHT_CLASS.map((f) => (
                            <option key={f.name} value={f.name}>
                              {f.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label">Item Nmfc No</label>
                        <Field
                          name="nmfc"
                          className="form__input form__input--sm"
                          placeholder="Item Nmfc No"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="formRow">
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label">Comments</label>
                        <Field
                          name="comments"
                          as="textarea"
                          className="form__input form__input--sm"
                          placeholder="Comments"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="btn__group">
                    <button
                      className="btn btn__white"
                      type="button"
                      onClick={goBack}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn__primary"
                      type="button"
                      onClick={submitForm}
                      disabled={isButtonDisabled(values, dirty)}
                    >
                      {id ? "Update" : "Save"}
                    </button>
                  </div>
                </section>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      {showModal && (
        <ConfirmModal
          confirmInfo={{
            heading: "Unsaved Changes!",
            para: "You have not saved your changes, do you want to continue",
          }}
          onConfirm={handleConfirmNavigation}
          setConfirmModal={handleCancelNavigation}
          firstMessageShow={true}
        />
      )}
      {loading && <Loader />}
    </>
  );
};

export default AddEditCustomerItems;
